import { AlexaAssessmentResult, AlexaDimensionScore } from './alexa-readiness-utils';
import { AuditedProduct } from './scoring-engine-constants';

/**
 * Persistence for Alexa for Shopping readiness assessments — kept separate from
 * the rewrite-proposal store since an assessment is a MEASUREMENT of the
 * listing as-is, whereas a proposal is a REMEDIATION.
 *
 * Each assessment records the content hash it was computed against, so a stale
 * assessment (listing content changed since) is detected and ignored rather
 * than silently shown as current.
 */

const ASSESSMENTS_STORAGE_KEY = 'pdp-scorecard-alexa-assessments-v1';

export interface AlexaAssessment extends AlexaAssessmentResult {
  productId: string;
  contentHash: string;
  assessedAt: number;
}

export type AlexaAssessmentStore = Record<string, AlexaAssessment>;

/** Deterministic djb2 hash of the scored content fields — not used for security, only staleness detection. */
export const computeContentHash = (product: AuditedProduct): string => {
  const basis = [
    product.title ?? '',
    product.featureBullets.join('|'),
    product.fullDescription ?? ''
  ].join('');

  let hash = 5381;
  for (let i = 0; i < basis.length; i++) {
    // djb2 hash — bitwise ops are intrinsic to the algorithm, not incidental.
    // eslint-disable-next-line no-bitwise
    hash = ((hash << 5) + hash + basis.charCodeAt(i)) | 0;
  }
  // eslint-disable-next-line no-bitwise
  return (hash >>> 0).toString(16);
};

const isAlexaDimensionScore = (v: unknown): v is AlexaDimensionScore =>
  !!v &&
  typeof v === 'object' &&
  typeof (v as Record<string, unknown>).key === 'string' &&
  typeof (v as Record<string, unknown>).label === 'string' &&
  typeof (v as Record<string, unknown>).score === 'number' &&
  typeof (v as Record<string, unknown>).rationale === 'string';

const isAlexaAssessment = (v: unknown): v is AlexaAssessment => {
  if (!v || typeof v !== 'object') return false;
  const a = v as Record<string, unknown>;
  return (
    typeof a.productId === 'string' &&
    typeof a.contentHash === 'string' &&
    typeof a.assessedAt === 'number' &&
    typeof a.score === 'number' &&
    typeof a.summary === 'string' &&
    Array.isArray(a.dimensions) &&
    a.dimensions.every(isAlexaDimensionScore)
  );
};

/** Drops any entries that don't match the expected shape (stale schema, hand-edited
 * localStorage) instead of letting them crash the render pipeline downstream. */
const parseAssessmentStore = (parsed: unknown): AlexaAssessmentStore => {
  if (typeof parsed !== 'object' || parsed === null) return {};
  const next: AlexaAssessmentStore = {};
  for (const [id, assessment] of Object.entries(parsed as Record<string, unknown>)) {
    if (isAlexaAssessment(assessment)) {
      next[id] = assessment;
    }
  }
  return next;
};

export const readAssessmentStore = (): AlexaAssessmentStore => {
  try {
    const raw = localStorage.getItem(ASSESSMENTS_STORAGE_KEY);
    if (!raw) return {};
    return parseAssessmentStore(JSON.parse(raw));
  } catch {
    return {};
  }
};

export const writeAssessmentStore = (store: AlexaAssessmentStore): void => {
  try {
    localStorage.setItem(ASSESSMENTS_STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Quota / disabled — non-fatal; in-memory state still works.
  }
};

export const clearAssessmentStore = (): void => {
  try {
    localStorage.removeItem(ASSESSMENTS_STORAGE_KEY);
  } catch {
    // ignore
  }
};

export const buildAssessment = (
  product: AuditedProduct,
  result: AlexaAssessmentResult,
  now: number = Date.now()
): AlexaAssessment => ({
  ...result,
  productId: product.id,
  contentHash: computeContentHash(product),
  assessedAt: now
});

/** Returns the stored assessment only if the listing's content hasn't changed since. */
export const getValidAssessment = (
  store: AlexaAssessmentStore,
  product: AuditedProduct
): AlexaAssessment | null => {
  const existing = store[product.id];
  if (!existing) return null;
  if (existing.contentHash !== computeContentHash(product)) return null;
  return existing;
};

export const isAssessmentStale = (
  store: AlexaAssessmentStore,
  product: AuditedProduct
): boolean => {
  const existing = store[product.id];
  return !!existing && existing.contentHash !== computeContentHash(product);
};

export interface AlexaReadinessSummary {
  assessedCount: number;
  totalCount: number;
  averageScore: number | null;
  dimensionAverages: { key: AlexaDimensionScore['key']; label: string; average: number }[];
}

/** Aggregates readiness across the portfolio using ONLY real (non-stale) assessments. */
export const summarizeAlexaReadiness = (
  products: AuditedProduct[],
  store: AlexaAssessmentStore
): AlexaReadinessSummary => {
  const valid: AlexaAssessment[] = [];
  for (const product of products) {
    const a = getValidAssessment(store, product);
    if (a) {
      valid.push(a);
    }
  }

  if (valid.length === 0) {
    return {
      assessedCount: 0,
      totalCount: products.length,
      averageScore: null,
      dimensionAverages: []
    };
  }

  const averageScore = Math.round(valid.reduce((acc, a) => acc + a.score, 0) / valid.length);

  const dimAcc = new Map<string, { label: string; sum: number; n: number }>();
  for (const a of valid) {
    for (const d of a.dimensions) {
      const cur = dimAcc.get(d.key) ?? { label: d.label, sum: 0, n: 0 };
      cur.sum += d.score;
      cur.n += 1;
      dimAcc.set(d.key, cur);
    }
  }
  const dimensionAverages = Array.from(dimAcc.entries()).map(([key, v]) => ({
    key: key as AlexaDimensionScore['key'],
    label: v.label,
    average: v.n > 0 ? Math.round((v.sum / v.n) * 10) / 10 : 0
  }));

  return {
    assessedCount: valid.length,
    totalCount: products.length,
    averageScore,
    dimensionAverages
  };
};
